<template>
  <div class="assessment-desktop">
    <div v-if="loading" class="state-card">
      <v-progress-circular indeterminate color="#6B35C8"></v-progress-circular>
    </div>

    <div v-else-if="!courses.length" class="state-card">
      <v-icon color="#6B35C8" large>mdi-clipboard-text-search-outline</v-icon>
      <div>هنوز دوره ای برای ارزیابی ندارید.</div>
    </div>

    <div v-else class="page">
      <header class="top">
        <div class="who">
          <div class="avatar">{{ student.avatar_initials || initials(student.name) }}</div>
          <div>
            <div class="name">{{ student.name }}</div>
          </div>
        </div>

        <nav class="courses">
          <button
              v-for="course in courses"
              :key="course.class_id"
              type="button"
              class="course"
              :class="{ active: course.class_id === selectedClassId }"
              @click="selectCourse(course)"
          >
            <span class="status-dot" :class="course.pace.key"></span>
            {{ course.title }}
          </button>
        </nav>
      </header>

      <main class="bento" v-if="selectedCourse">
        <section class="card course-card">
          <h3 class="mb-8">مشخصات دوره</h3>
          <div class="lessons">
            <div class="row">
              <span class="k mb-3">درس‌ها</span>
              <span class="v">
                {{ faNumber(selectedCourse.opened_lessons) }}
                <small> از {{ faNumber(selectedCourse.total_lessons) }}</small>
              </span>
            </div>
            <div class="bar"><i :style="{ width: lessonProgressPercent + '%' }"></i></div>
          </div>
          <div class="facts">
            <div class="fact">
              <span class="k">دوره</span>
              <span class="v">{{ selectedCourse.course_type || 'دوره آموزشی' }}</span>
            </div>
            <div class="fact">
              <span class="k">مربی</span>
              <span class="v">{{ selectedCourse.instructor || 'ثبت نشده' }}</span>
            </div>
            <div class="fact">
              <span class="k">شروع</span>
              <span class="v">{{ selectedCourse.start_date || 'ثبت نشده' }}</span>
            </div>
            <div class="fact">
              <span class="k">امروز</span>
              <span class="v">روز {{ faNumber(selectedCourse.day_of_course) }} ام</span>
            </div>
          </div>
        </section>

        <section class="card week-card">
          <div class="row">
            <div>
              <h3>این هفته</h3>
              <div class="sub">
                هدف {{ faNumber(weeklySummary.target) }} روز ·
                {{ faNumber(weeklySummary.days_to_end) }} روز تا پایان هفته
              </div>
            </div>
            <span class="week-status" :class="weeklySummary.status.key">
              <span class="week-status-dot"></span>
              {{ weeklySummary.status.label }}
            </span>
          </div>

          <div class="week-figs">
            <div class="fig">
              <div class="n">{{ faNumber(weeklySummary.practice_days) }}</div>
              <div class="l">روز تمرین</div>
            </div>
            <div class="fig">
              <div class="n">{{ faNumber(weeklySummary.total_score) }}</div>
              <div class="l">امتیاز</div>
            </div>
          </div>

          <div class="week-dots">
            <div v-for="day in weeklySummary.days" :key="day.date" class="week-dot-col">
              <div
                  class="week-dot"
                  :class="{ done: day.done, future: day.is_future, today: day.is_today }"
              ></div>
              <span>{{ day.letter }}</span>
            </div>
          </div>
        </section>

        <section class="card month-card">
          <h3>{{ monthScoreTitle }}</h3>
          <div class="month-score">{{ faNumber(calendar.total_score) }}</div>
          <v-menu v-if="socialEnabled && Number(calendar.total_score) > 0" offset-y>
            <template v-slot:activator="{ on, attrs }"><v-btn small text color="#6B35C8" v-bind="attrs" v-on="on"><v-icon left small>mdi-share-variant</v-icon>اشتراک دستاورد</v-btn></template>
            <v-list dense><v-list-item @click="shareAchievement('class')"><v-list-item-title>هم‌کلاسی‌ها</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('course')"><v-list-item-title>هم‌دوره‌ای‌ها</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('all')"><v-list-item-title>همه هنرجوهای درک</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('public')"><v-list-item-title>عمومی با لینک</v-list-item-title></v-list-item></v-list>
          </v-menu>
<!--
          <div class="sub month-sub">{{ faNumber(calendar.practice_days_count) }} روز تمرین در {{ monthName }}</div>
-->

          <div class="spark" aria-label="امتیاز روزانه ماه">
            <i
                v-for="bar in sparkBars"
                :key="bar.key"
                :class="bar.className"
                :style="{ height: bar.height + 'px' }"
                :title="bar.title"
            ></i>
          </div>

          <div class="month-foot">
            <span><b>{{ faNumber(calendar.practice_days_count) }}</b> روز تمرین در {{ monthName }}</span>
            <span class="streak-pill" title="روزهای پشت سر هم با تمرین">
              <svg viewBox="0 0 11 13" aria-hidden="true">
                <path d="M5.5 0C6 2.6 9.5 4.2 9.5 7.8 9.5 10.6 7.7 13 5.5 13S1.5 10.6 1.5 7.8c0-1.7.8-2.8 1.6-3.6.2 1 .7 1.7 1.4 1.9C4 4.3 5 2.2 5.5 0z" fill="#F5920A"/>
              </svg>
              {{ faNumber(calendar.streak_days) }} روز زنجیره
            </span>
          </div>
        </section>

        <section class="card calendar-card">
          <div class="calendar-head">
            <div>
              <h3>تقویم تلاش روزانه</h3>
              <div class="sub">{{ calendar.month_title }}</div>
            </div>
            <div class="calendar-actions">
              <button type="button" :disabled="calendarLoading" @click="changeMonth(calendar.previous_month)" title="ماه قبل">‹</button>
              <button type="button" :disabled="calendarLoading" @click="changeMonth(calendar.next_month)" title="ماه بعد">›</button>
            </div>
          </div>

          <div class="calendar-grid" :class="{ loading: calendarLoading }">
            <div v-for="weekday in weekdays" :key="weekday" class="weekday">{{ weekday }}</div>
            <div v-for="index in calendar.start_offset" :key="'empty-' + index" class="day empty"></div>
            <div
                v-for="day in calendar.days"
                :key="day.day"
                class="day"
                :class="{ today: day.is_today, future: isFutureDay(day) }"
                :style="dayStyle(day)"
                :title="dayTitle(day)"
            >
              <span class="date">{{ faNumber(day.day) }}</span>
              <span v-if="day.score > 0" class="score">{{ faNumber(day.score) }}</span>
            </div>
          </div>

          <div class="legend">
            <span
                v-for="level in calendar.levels"
                :key="level.index"
                :style="{ backgroundColor: level.color }"
                :title="level.label"
                :class="{ bordered: level.index === 0 }"
            ></span>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "AssessmentsDesktop",
  data() {
    return {
      loading: true,
      calendarLoading: false,
      student: {},
      courses: [],
      selectedClassId: null,
      selectedMonth: null,
      socialEnabled: false,
      weekdays: ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']
    }
  },
  computed: {
    selectedCourse() {
      return this.courses.find(course => course.class_id === this.selectedClassId) || this.courses[0]
    },
    calendar() {
      return this.selectedCourse?.calendar || this.defaultCalendar()
    },
    weeklySummary() {
      return this.selectedCourse?.weekly_summary || this.defaultWeeklySummary()
    },
    lessonProgressPercent() {
      return Math.max(0, Math.min(100, Number(this.selectedCourse?.progress_percent || 0)))
    },
    monthName() {
      return String(this.calendar.month_title || '').split(/\s+/)[0] || 'ماه'
    },
    monthScoreTitle() {
      return `امتیاز ${this.monthName}`
    },
    sparkBars() {
      const days = this.calendar.days || []
      const maxScore = Math.max(...days.map(day => Number(day.score || 0)), 1)

      return days.map(day => {
        const score = Number(day.score || 0)
        const height = score > 0 ? Math.max(8, Math.round(score / maxScore * 40)) : 3
        const classes = []
        if (score <= 0) {
          classes.push('zero')
        }
        if (day.is_today) {
          classes.push('today')
        }

        return {
          key: day.day,
          height,
          className: classes.join(' '),
          title: `${this.faNumber(day.day)} ${this.monthName} - ${score > 0 ? this.faNumber(score) + ' امتیاز' : 'بدون تمرین'}`
        }
      })
    }
  },
  mounted() {
    this.getAssessment(null, true)
  },
  watch: { selectedClassId() { this.checkSocialAvailability() } },
  methods: {
    checkSocialAvailability() {
      if (!this.selectedCourse) { this.socialEnabled = false; return }
      window.axios.get('student/social/status', { params: { class_id: this.selectedCourse.class_id, course_id: this.selectedCourse.course_id } }).then(res => { this.socialEnabled = !!res.data.enabled }).catch(() => { this.socialEnabled = false })
    },
    shareAchievement(audience) {
      if (!this.selectedCourse) return
      window.axios.post('student/social/posts', {
        post_type: 'achievement', audience, online_class_id: this.selectedCourse.class_id,
        course_id: this.selectedCourse.course_id,
        content: `دستاورد من در ${this.selectedCourse.title}: ${this.calendar.total_score} امتیاز در ${this.monthName} ⭐`
      }).then(res => this.openSnackbar(res.data.status === 'pending' ? 'دستاورد برای تأیید ارسال شد.' : 'دستاوردت در شبکه اجتماعی منتشر شد.', '#6B35C8'))
          .catch(err => this.openSnackbar(err.response?.data?.message || 'اشتراک‌گذاری انجام نشد.', '#F1416C'))
    },
    getAssessment(month = this.selectedMonth, initialLoad = false) {
      if (initialLoad) {
        this.loading = true
      } else {
        this.calendarLoading = true
      }

      window.axios.get('student/assessment/details', {
        params: month ? {month} : {}
      }).then((res) => {
        const data = res.data.data || {}
        const previousClassId = this.selectedClassId
        this.student = data.student || {}
        this.courses = data.courses || []
        this.selectedClassId = this.courses.some(course => course.class_id === previousClassId)
            ? previousClassId
            : this.courses[0]?.class_id || null
        this.selectedMonth = this.calendar.month || month || null
      }).catch((err) => {
        this.openSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات ارزیابی', '#F1416C')
      }).finally(() => {
        this.loading = false
        this.calendarLoading = false
      })
    },
    defaultCalendar() {
      return {
        month_title: '',
        start_offset: 0,
        practice_days_count: 0,
        streak_days: 0,
        total_score: 0,
        today: null,
        previous_month: null,
        next_month: null,
        levels: [],
        days: []
      }
    },
    defaultWeeklySummary() {
      return {
        target: 4,
        days_to_end: 0,
        practice_days: 0,
        total_score: 0,
        status: {key: 'good', label: 'خوب'},
        days: ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((letter, index) => ({
          date: String(index),
          letter,
          score: null,
          is_future: true,
          is_today: false,
          done: false
        }))
      }
    },
    selectCourse(course) {
      this.selectedClassId = course.class_id
    },
    changeMonth(month) {
      if (!month || this.calendarLoading) {
        return
      }

      this.selectedMonth = month
      this.getAssessment(month)
    },
    dayStyle(day) {
      if (this.isFutureDay(day)) {
        return {}
      }

      return {
        backgroundColor: day.color || '#E9E8F0',
        color: this.readableTextColor(day.color || '#E9E8F0')
      }
    },
    isFutureDay(day) {
      return Boolean(this.calendar.today && day && Number(day.day) > Number(this.calendar.today))
    },
    dayTitle(day) {
      return `${this.faNumber(day.score || 0)} امتیاز${day.label ? ' - ' + day.label : ''}`
    },
    readableTextColor(hex) {
      const value = String(hex || '#ffffff').replace('#', '')
      const r = parseInt(value.substring(0, 2), 16)
      const g = parseInt(value.substring(2, 4), 16)
      const b = parseInt(value.substring(4, 6), 16)
      return ((r * 299 + g * 587 + b * 114) / 1000) > 150 ? '#1A0A3C' : '#FFFFFF'
    },
    faNumber(value) {
      return String(value ?? 0).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    initials(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) {
        return 'هـ'
      }
      return parts.length > 1 ? `${parts[0][0]}‌${parts[1][0]}` : parts[0][0]
    }
  }
}
</script>

<style scoped>
.assessment-desktop {
  min-height: 100vh;
  color: #1A0A3C;
  direction: rtl;
  background: radial-gradient(900px 520px at 88% -8%, #E6DCFB 0%, rgba(230, 220, 251, 0) 60%),
  radial-gradient(700px 480px at 4% 108%, #FFEBD2 0%, rgba(255, 235, 210, 0) 60%),
  #F4F2F9;
}

.page {
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 24px 56px;
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}

.who {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  color: #6B35C8;
  box-shadow: 0 1px 2px rgba(26, 10, 60, .04), 0 12px 32px rgba(26, 10, 60, .05);
  flex-shrink: 0;
}

.name {
  font-size: 19px;
  font-weight: 800;
}

.mobile {
  color: #8C8C9A;
  font-size: 12px;
  margin-top: 3px;
}

.courses {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, .75);
  border: 1px solid rgba(26, 10, 60, .06);
  padding: 4px;
  border-radius: 99px;
}

.course {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: #5A5270;
  padding: 7px 15px;
  border-radius: 99px;
  cursor: pointer;
  white-space: nowrap;
  border: 0;
  background: transparent;
  font-family: inherit;
}

.course:hover {
  background: rgba(107, 53, 200, .06);
}

.course.active {
  background: #1A0A3C;
  color: #fff;
  font-weight: 700;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.excellent {
  background: #22C55E;
}

.status-dot.ok {
  background: #F5920A;
}

.status-dot.needs_practice {
  background: #EF4444;
}

.bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  align-items: stretch;
}

.card,
.state-card {
  background: #fff;
  border-radius: 26px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(26, 10, 60, .04), 0 12px 32px rgba(26, 10, 60, .05);
  position: relative;
  overflow: hidden;
}

.state-card {
  min-height: 360px;
  margin: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 14px;
  color: #1A0A3C;
  font-weight: 800;
}

.card h3 {
  font-size: 14px;
  font-weight: 800;
  line-height: 1.5;
  margin: 0;
}

.sub {
  font-size: 11.5px;
  font-weight: 600;
  color: #8C8C9A;
}

.row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.course-card {
  grid-column: 1;
  grid-row: 1;
}

.month-card {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
}

.week-card {
  grid-column: 1 / 3;
  grid-row: 2;
  background: #1A0A3C;
  color: #fff;
}

.calendar-card {
  grid-column: 3 / 5;
  grid-row: 1 / 3;
  display: flex;
  flex-direction: column;
}

.week-card::before {
  content: "";
  position: absolute;
  width: 380px;
  height: 380px;
  left: -170px;
  top: -210px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(107, 53, 200, .7) 0%, rgba(107, 53, 200, 0) 70%);
}

.week-card::after {
  content: "";
  position: absolute;
  width: 240px;
  height: 240px;
  right: -60px;
  bottom: -190px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(245, 146, 10, .22) 0%, rgba(245, 146, 10, 0) 70%);
}

.week-card > * {
  position: relative;
  z-index: 1;
}

.week-card .sub {
  color: rgba(255, 255, 255, .5);
}

.week-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 99px;
  white-space: nowrap;
}

.week-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.week-status.great {
  background: rgba(90, 204, 135, .18);
  color: #9BE8B8;
}

.week-status.good {
  background: rgba(240, 232, 255, .14);
  color: #D9C8FF;
}

.week-status.low {
  background: rgba(245, 146, 10, .18);
  color: #FFC57A;
}

.week-figs {
  display: flex;
  gap: 28px;
  margin-top: 20px;
}

.fig .n {
  font-size: 28px;
  font-weight: 400;
  line-height: 1.1;
}

.fig .l {
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, .55);
  margin-top: 4px;
}

.fig + .fig {
  border-right: 1px solid rgba(255, 255, 255, .12);
  padding-right: 28px;
}

.week-dots {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-top: 22px;
}

.week-dot-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
}

.week-dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .09);
}

.week-dot.done {
  background: #5ACC87;
}

.week-dot.future {
  background: transparent;
  border: 1.5px dashed rgba(255, 255, 255, .22);
}

.week-dot.today {
  box-shadow: 0 0 0 2px #1A0A3C, 0 0 0 3.5px #F5920A;
}

.week-dot-col span {
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, .45);
}

.lessons {
  margin-top: 14px;
}

.lessons .row {
  align-items: baseline;
}

.lessons .k,
.fact .k {
  font-size: 12px;
  font-weight: 600;
  color: #8C8C9A;
}

.lessons .v {
  font-size: 15px;
  font-weight: 800;
}

.lessons .v small {
  font-size: 12px;
  font-weight: 600;
  color: #B5B5C0;
}

.bar {
  height: 6px;
  border-radius: 99px;
  background: #F0E8FF;
  margin-top: 8px;
  overflow: hidden;
}

.bar i {
  display: block;
  height: 100%;
  background: #6B35C8;
  border-radius: 99px;
}

.facts {
  margin-top: 14px;
}

.fact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-top: 1px solid #F0EEF5;
  font-size: 12px;
}

.fact:last-child {
  padding-bottom: 0;
}

.fact .v {
  font-weight: 700;
  color: #1A0A3C;
  text-align: left;
}

.month-score {
  font-size: 38px;
  font-weight: 500;
  line-height: 1;
  text-align: right;
  margin-top: 14px;
}

.month-sub {
  margin-top: 8px;
  text-align: center;
}

.spark {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 40px;
  margin-top: 16px;
}

.spark i {
  flex: 1;
  border-radius: 4px 4px 1px 1px;
  background: #6B35C8;
  min-height: 3px;
  position: relative;
}

.spark i.zero {
  background: #E4E1EC;
}

.spark i.today::after {
  content: "";
  position: absolute;
  bottom: -9px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #F5920A;
}

.month-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: #8C8C9A;
  margin-top: auto;
  padding-top: 16px;
}

.month-foot b {
  color: #1A0A3C;
  font-weight: 800;
}

.streak-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: #9A4D00;
  background: #FFF1DE;
  padding: 4px 10px 4px 9px;
  border-radius: 99px;
  white-space: nowrap;
}

.streak-pill svg {
  width: 11px;
  height: 13px;
  flex-shrink: 0;
}

.calendar-card {
  padding: 24px;
}

.calendar-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
}

.calendar-actions {
  display: flex;
  gap: 6px;
}

.calendar-actions button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #ECEAF2;
  background: #fff;
  color: #4E4566;
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
}

.calendar-actions button:hover:not(:disabled) {
  background: #F0E8FF;
  border-color: #6B35C8;
  color: #6B35C8;
}

.calendar-actions button:disabled {
  opacity: .35;
  cursor: progress;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  transition: opacity .2s ease;
}

.calendar-grid.loading {
  opacity: .55;
  pointer-events: none;
}

.weekday {
  text-align: center;
  font-size: 11px;
  color: #B5B5C0;
  font-weight: 600;
  padding-bottom: 2px;
}

.day {
  aspect-ratio: 1 / 1;
  border-radius: 13px;
  position: relative;
  background: #E9E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day .date {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1;
  color: #7C7C8C;
}

.day .score {
  margin-top: 7px;
  font-size: 17px;
  font-weight: 800;
  line-height: 1;
  color: #0F5A30;
}

.day.p1 .date,
.day.p2 .date,
.day.p3 .date {
  color: rgba(15, 90, 48, .65);
}

.day.future {
  background: transparent !important;
  border: 1px dashed #D3D0DE;
}

.day.future .date {
  color: #B3B3C0;
}

.day.today {
  box-shadow: inset 0 0 0 2px #F5920A;
}

.day.empty {
  background: transparent !important;
  box-shadow: none;
}

.legend {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: auto;
  padding-top: 16px;
}

.legend span {
  width: 15px;
  height: 15px;
  border-radius: 5px;
}

.legend .bordered {
  border: 1px solid #E0E0E0;
}

@media (max-width: 1100px) {
  .bento {
    grid-template-columns: 1fr 1fr;
  }

  .course-card {
    grid-column: 1;
    grid-row: 1;
  }

  .month-card {
    grid-column: 2;
    grid-row: 1;
  }

  .week-card {
    grid-column: 1 / 3;
    grid-row: 2;
  }

  .calendar-card {
    grid-column: 1 / 3;
    grid-row: 3;
    max-width: 620px;
    justify-self: center;
    width: 100%;
  }
}

@media (max-width: 680px) {
  .page {
    padding: 20px 16px 40px;
  }

  .top {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .courses {
    max-width: 100%;
    overflow-x: auto;
  }

  .bento {
    grid-template-columns: 1fr;
  }

  .week-card,
  .course-card,
  .calendar-card,
  .month-card {
    grid-column: 1;
    grid-row: auto;
  }

  .week-dot {
    width: 30px;
    height: 30px;
  }

  .calendar-card {
    padding: 20px 14px;
  }

  .calendar-grid {
    gap: 6px;
  }

  .day {
    aspect-ratio: 4 / 5;
    border-radius: 12px;
  }

  .day .score {
    font-size: 15px;
  }

  .day .date {
    font-size: 10px;
    top: 5px;
    right: 7px;
  }
}
</style>
